import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Fab,
  Paper,
  CircularProgress,
  Button,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from "framer-motion";
// Icons used
import CloseIcon from "@mui/icons-material/Close";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ErrorIcon from "@mui/icons-material/Error";
import ReplayIcon from "@mui/icons-material/Replay";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import { useMsal } from "@azure/msal-react";
import {
  startConversation,
  followUp,
  feedback,
} from "Samarth Module/api/chatBotAPI";
import QueryResultTable from "./QueryResultTable";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import MaximizeIcon from "@mui/icons-material/Maximize";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ChatFilter from "./ChatFilter";
import SamarthAIDrawer from "../SamarthAi/SamarthAIDrawer";
import AIIcon from "../../../assets/images/chat_AI_icon.svg";

const ChatBot = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [dots, setDots] = useState("");
  const [error, setError] = useState(null);
  const [lastUserInput, setLastUserInput] = useState("");
  const firstMessage = useRef(true);
  const [conversationId, setConversationId] = useState("");
  const [spaceId, setSpaceId] = useState("");
  const [maximize, setMaximize] = useState(false);
  const [openFeedbackform, setOpenFeedbackForm] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [queryResultMap, setQueryResultMap] = useState({});
  const [formValues, setFormValues] = useState({});

  const inputRef = useRef(null);

  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const [firstName, setFirstName] = useState("");

  const getName = () => {
    const nameParts = activeAccount.name?.split(", ") || [];
    if (nameParts.length > 1) {
      setFirstName(nameParts[1]);
    } else {
      setFirstName(activeAccount.name || "");
    }
  };

  const toggleChat = () => setChatOpen((prev) => !prev);

  //"error": "Error code: 400 - {'error': {'message': \"This model's maximum context length is 128000 tokens. However, your messages resulted in 205000 tokens. Please reduce the length of the messages.\", 'type': 'invalid_request_error', 'param': 'messages', 'code': 'context_length_exceeded'}}"

  const extractError = (err) => {
    const errorString = err?.response?.data?.error;
    if (errorString) {
      const match = errorString.match(/'message': "(.*?)"/);
      if (match && match[1]) {
        return match[1];
      } else {
        return err.message;
      }
    } else {
      return err?.message || "Something went wrong";
    }
  };

  const attachFilterInPrompt = () => {
    let filters = "";
    for (let key of Object.keys(formValues)) {
      filters += `${key}=${formValues[key].join(",")}; `;
    }
    return filters;
  };

  const handleSend = async (e, customInput = null) => {
    const currentInput = customInput || input.trim();

    if (!currentInput) return;

    if (loading) return;

    const userMessage = { text: currentInput, type: "user" };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setTyping(true);
    setLoading(true);
    setError(null);
    setLastUserInput(currentInput);

    let botMessage = "";
    let queryResult = "";
    let messageId = "";

    const userPrompt = `${currentInput} ${attachFilterInPrompt()}`;

    if (firstMessage.current === true) {
      try {
        const res = await startConversation(
          userPrompt,
          12345,
          activeAccount.name,
          activeAccount.username
        );
        if (res.conversation_id) {
          setConversationId(res.conversation_id);
        }
        if (res.space_id) {
          setSpaceId(res.space_id);
        }

        if (res.explanation) {
          botMessage = res.explanation;
        }

        if (res?.query_result) {
          queryResult = res.query_result;
        }

        if (res.message_id) {
          messageId = res.message_id;
        }

        firstMessage.current = false;
        setTyping(false);
        setLoading(false);
      } catch (error) {
        const formattedError = extractError(error);
        setError(formattedError);
        setTyping(false);
        setLoading(false);
      }
    } else if (firstMessage.current === false) {
      try {
        const res = await followUp(
          conversationId,
          spaceId,
          userPrompt,
          12345,
          activeAccount.name,
          activeAccount.username
        );
        if (res.conversation_id) {
          setConversationId(res.conversation_id);
        }
        if (res.space_id) {
          setSpaceId(res.space_id);
        }
        if (res.explanation) {
          botMessage = res.explanation;
        }

        if (res?.query_result) {
          queryResult = res.query_result;
        }

        if (res.message_id) {
          messageId = res.message_id;
        }

        setTyping(false);
        setLoading(false);
      } catch (error) {
        const formattedError = extractError(error);
        setError(formattedError);
        setTyping(false);
        setLoading(false);
      }
    }

    if (botMessage) {
      setMessages((prev) => [
        ...prev,
        { text: botMessage, messageId, type: "bot" }, // No queryResult here
      ]);

      if (queryResult && messageId) {
        setTimeout(() => {
          setQueryResultMap((prev) => ({
            ...prev,
            [messageId]: queryResult,
          }));
        }, 50); // delay to avoid UI freeze
      }
    }
  };

  const handleFeedback = async (
    messageId,
    feedbackType,
    feedbackComment = ""
  ) => {
    try {
      const res = await feedback(
        conversationId,
        messageId,
        spaceId,
        feedbackType,
        feedbackComment
      );
      console.log("res", res);
    } catch (error) {
      const formattedError = extractError(error);
      setError(formattedError);
      setTyping(false);
      setLoading(false);
    }
  };

  const createNewChat = () => {
    setMessages([
      { text: `Hi! ${firstName}, how can I help you today?`, type: "bot" },
    ]);
    firstMessage.current = true;
    setConversationId("");
    setSpaceId("");
    if (error) {
      setError(null);
    }
    setFormValues({});
  };

  useEffect(() => {
    if (typing) {
      const interval = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + "." : ""));
      }, 500);

      return () => clearInterval(interval);
    } else {
      setDots("");
    }
  }, [typing]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, typing]);

  useEffect(() => {
    if (accounts.length > 0) {
      getName();
    }
    //eslint-disable-next-line
  }, [accounts]);

  useEffect(() => {
    setMessages([
      { text: `Hi! ${firstName}, how can I help you today?`, type: "bot" },
    ]);
  }, [firstName]);

  useEffect(() => {
    if (chatOpen) {
      inputRef.current?.focus();
    }
  }, [chatOpen]);

  return (
    <>
      {/* Floating Chat Icon */}
      <motion.div
        key={chatOpen ? "chat-open" : "chat-closed"}
        drag="x"
        dragConstraints={{ left: -800, right: 0 }}
        onDragStart={() => {
          isDraggingRef.current = true;
          setIsDragging(true);
        }}
        onDragEnd={() => {
          setTimeout(() => {
            isDraggingRef.current = false;
            setIsDragging(false);
          }, 50);
        }}
        animate={{ y: [0, -5, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1300,
          height: "56px",
          width: "56px",
        }}
      >
        <Fab
          onClick={() => {
            if (!isDraggingRef.current) {
              toggleChat();
              localStorage.removeItem("convId");
            }
          }}
          sx={{
            p: 0,
            m: 0,
            minHeight: "56px",
            minWidth: "56px",
            height: "56px",
            width: "56px",
            borderRadius: "50%",
            boxShadow: "none",
            backgroundColor: "transparent",
            cursor: isDragging ? "grabbing" : "pointer",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          {chatOpen ? (
            <CloseIcon size={20} />
          ) : (
            <Box
              component="img"
              src={AIIcon}
              sx={{
                height: "56px",
                width: "56px",
                objectFit: "contain",
              }}
            />
          )}
        </Fab>
      </motion.div>

      {/* Chat Panel */}
      <AnimatePresence>
        {chatOpen && (
          // <motion.div
          //   initial={{ opacity: 0, y: 50, scale: 0.95 }}
          //   animate={{ opacity: 1, y: 0, scale: 1 }}
          //   exit={{ opacity: 0, y: 50, scale: 0.95 }}
          //   transition={{ duration: 0.3 }}
          //   style={{
          //     position: "fixed",
          //     bottom: 90,
          //     right: 24,
          //     zIndex: 1300,
          //     width: maximize ? "90%" : 360,
          //   }}
          // >
          //   <Paper
          //     elevation={6}
          //     sx={{
          //       height: maximize ? "75vh" : "60vh",
          //       display: "flex",
          //       flexDirection: "column",
          //       borderRadius: 4,
          //       overflow: "hidden",
          //       backdropFilter: "blur(20px)",
          //       WebkitBackdropFilter: "blur(20px)",
          //       boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
          //     }}
          //   >
          //     <Box
          //       sx={{
          //         py: 1,
          //         backgroundColor: "var(--primary-unilever-color)",
          //         color: "#fff",
          //         textAlign: "center",
          //         position: "relative",
          //       }}
          //     >
          //       <IconButton
          //         sx={{
          //           position: "absolute",
          //           left: 6,
          //           top: 2,
          //           color: "#fff",
          //         }}
          //         onClick={() => setMaximize(!maximize)}
          //       >
          //         {maximize ? (
          //           <MaximizeIcon fontSize="small" />
          //         ) : (
          //           <CropSquareIcon fontSize="small" />
          //         )}
          //       </IconButton>
          //       <Typography fontSize="14px" fontWeight={600}>
          //         Samarth AI Chat Bot
          //       </Typography>
          //       <Tooltip title="New chat">
          //         <IconButton
          //           sx={{
          //             position: "absolute",
          //             right: 6,
          //             top: 2,
          //             color: "#fff",
          //           }}
          //           onClick={createNewChat}
          //         >
          //           <AddIcon fontSize="small" />
          //         </IconButton>
          //       </Tooltip>
          //     </Box>
          //     {maximize && (
          //       <ChatFilter
          //         formValues={formValues}
          //         setFormValues={setFormValues}
          //       />
          //     )}
          //     <Box
          //       sx={{
          //         flex: 1,
          //         p: 2,
          //         overflowY: "auto",
          //         display: "flex",
          //         flexDirection: "column",
          //         gap: 1.5,
          //         scrollbarWidth: "none",
          //         msOverflowStyle: "none",
          //         "&::-webkit-scrollbar": { display: "none" },
          //       }}
          //     >
          //       {messages.map((msg, idx) => (
          //         <motion.div
          //           key={idx}
          //           initial={{ opacity: 0, y: 10 }}
          //           animate={{ opacity: 1, y: 0 }}
          //           transition={{ delay: idx * 0.05 }}
          //           style={{
          //             alignSelf:
          //               msg.type === "user" ? "flex-end" : "flex-start",
          //             display: "flex",
          //             alignItems: "center",
          //             gap: "4px",
          //           }}
          //         >
          //           {msg.type === "bot" && (
          //             <Box
          //               sx={{
          //                 p: 1,
          //                 width: "20px",
          //                 height: "20px",
          //                 borderRadius: "50%",
          //                 boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          //                 color: "var(--primary-unilever-color)",
          //               }}
          //             >
          //               <SmartToyIcon fontSize="small" />
          //             </Box>
          //           )}
          //           <Box
          //             sx={{
          //               backgroundColor:
          //                 msg.type === "user" ? "#d1eaff" : "#ffffffaa",
          //               color: "var(--black-color)",
          //               px: 2,
          //               py: 1,
          //               borderRadius: 3,
          //               maxWidth: "90%",
          //               boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          //               display: "flex",
          //               alignItems: "start",
          //             }}
          //           >
          //             <Typography
          //               variant="body2"
          //               sx={{
          //                 fontSize: "12px",
          //                 textAlign: "left",
          //                 whiteSpace: "pre-line",
          //               }}
          //             >
          //               {msg.text}

          //               {/* Show Table */}
          //               {queryResultMap[msg.messageId] && (
          //                 <QueryResultTable
          //                   queryResult={queryResultMap[msg.messageId]}
          //                   maximize={maximize}
          //                 />
          //               )}

          //               {/* Down and Up Votes */}
          //               {msg.type === "bot" && msg.messageId && (
          //                 <Box
          //                   display="flex"
          //                   alignItems={"center"}
          //                   mt={2}
          //                   position={"relative"}
          //                 >
          //                   <IconButton
          //                     onClick={() => setOpenFeedbackForm(msg.messageId)}
          //                   >
          //                     <ThumbDownIcon fontSize="small" />
          //                   </IconButton>
          //                   <IconButton
          //                     onClick={() =>
          //                       handleFeedback(msg.messageId, "positive")
          //                     }
          //                     mb={1}
          //                   >
          //                     {" "}
          //                     <ThumbUpIcon fontSize="small" />
          //                   </IconButton>

          //                   {/* Open feedback form */}
          //                   {openFeedbackform &&
          //                     openFeedbackform === msg.messageId && (
          //                       <Box
          //                         sx={{
          //                           position: "absolute",
          //                           left: 0,
          //                           bottom: 0,
          //                           zIndex: 999,
          //                           width: "100%",
          //                           height: "40px",
          //                           px: 2,
          //                           py: 1,
          //                           display: "flex",
          //                           alignItems: "center",
          //                           gap: 1,
          //                           backgroundColor: "#f4f6fa",
          //                           borderRadius: "6px",
          //                         }}
          //                       >
          //                         <IconButton
          //                           onClick={() => {
          //                             setOpenFeedbackForm("");
          //                             handleFeedback(msg.messageId, "negative");
          //                           }}
          //                           size="small"
          //                           sx={{ color: "#000" }}
          //                         >
          //                           <CloseIcon fontSize="small" />
          //                         </IconButton>
          //                         <TextField
          //                           fullWidth
          //                           size="small"
          //                           placeholder="Enter your feedback"
          //                           variant="outlined"
          //                           value={feedbackComment}
          //                           onChange={(e) =>
          //                             setFeedbackComment(e.target.value)
          //                           }
          //                           sx={{
          //                             pr: 7,
          //                             "& .MuiInputBase-input": {
          //                               fontSize: "12px",
          //                             },
          //                           }}
          //                         />
          //                         <IconButton
          //                           onClick={() => {
          //                             handleFeedback(
          //                               msg.messageId,
          //                               "negative",
          //                               feedbackComment
          //                             );
          //                             setOpenFeedbackForm("");
          //                           }}
          //                           color="primary"
          //                           sx={{
          //                             position: "absolute",
          //                             right: 18,
          //                             top: "50%",
          //                             transform: "translateY(-50%)",
          //                             backgroundColor: "#0046be",
          //                             color: "white",
          //                             boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          //                             "&:hover": {
          //                               backgroundColor: "#003a9f",
          //                             },
          //                           }}
          //                         >
          //                           <SendIcon fontSize="small" />
          //                         </IconButton>
          //                       </Box>
          //                     )}
          //                 </Box>
          //               )}
          //             </Typography>
          //             {msg.type === "bot" && idx !== 0 && (
          //               <Tooltip title="Copy">
          //                 <IconButton
          //                   size="small"
          //                   onClick={() =>
          //                     navigator.clipboard.writeText(msg.text)
          //                   }
          //                   sx={{ p: 0.5, ml: 1 }}
          //                 >
          //                   <ContentCopyIcon sx={{ fontSize: 14 }} />
          //                 </IconButton>
          //               </Tooltip>
          //             )}
          //           </Box>
          //           {msg.type === "user" && (
          //             <Box
          //               sx={{
          //                 p: 1,
          //                 width: "20px",
          //                 height: "20px",
          //                 borderRadius: "50%",
          //                 boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          //                 color: "var(--speedo-meter-progress)",
          //               }}
          //             >
          //               <AccountCircleIcon fontSize="small" />
          //             </Box>
          //           )}
          //         </motion.div>
          //       ))}

          //       {typing && (
          //         <Box alignSelf="flex-start">
          //           <Typography
          //             variant="caption"
          //             color="text.secondary"
          //             component="span"
          //           >
          //             Generating answers
          //             {[...dots].map((dot, idx) => (
          //               <motion.span
          //                 key={idx}
          //                 initial={{ opacity: 0 }}
          //                 animate={{ opacity: 1 }}
          //                 transition={{ duration: 0.3, delay: idx * 0.15 }}
          //               >
          //                 {dot}
          //               </motion.span>
          //             ))}
          //           </Typography>
          //         </Box>
          //       )}
          //       <div ref={messagesEndRef} />
          //     </Box>

          //     {/* Error component */}
          //     {error && (
          //       <Box
          //         sx={{
          //           mx: 2,
          //           my: 1,
          //           p: 1,
          //           backgroundColor: "var(--high-critical)",
          //           color: "#fff",
          //           borderRadius: 2,
          //           fontWeight: 500,
          //           boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          //           display: "flex",
          //           justifyContent: "center",
          //           alignItems: "center",
          //           gap: "8px",
          //         }}
          //       >
          //         <ErrorIcon fontSize="small" />
          //         <Typography fontSize={"11px"}>{error}</Typography>
          //         <Button
          //           size="small"
          //           onClick={(e) => handleSend(e, lastUserInput)}
          //           sx={{
          //             backgroundColor: "#fff",
          //             color: "var(--high-critical)",
          //             fontSize: "11px !important",
          //             textTransform: "none",
          //             "&:hover": {
          //               backgroundColor: "#f5f5f5",
          //             },
          //             display: "flex",
          //             justifyContent: "center",
          //             gap: "2px",
          //           }}
          //         >
          //           <ReplayIcon fontSize="small" /> <span>Retry</span>
          //         </Button>
          //         <IconButton
          //           onClick={() => setError(null)}
          //           size="small"
          //           sx={{ color: "#fff", ml: 1 }}
          //         >
          //           <CloseIcon fontSize="small" />
          //         </IconButton>
          //       </Box>
          //     )}

          //     <Box
          //       sx={{
          //         px: 2,
          //         py: 1,
          //         display: "flex",
          //         alignItems: "center",
          //         position: "relative",
          //         backgroundColor: "#f4f6fa",
          //       }}
          //     >
          //       <TextField
          //         fullWidth
          //         size="small"
          //         placeholder="What you want ask..."
          //         variant="outlined"
          //         value={input}
          //         onChange={(e) => setInput(e.target.value)}
          //         onKeyDown={(e) => e.key === "Enter" && handleSend(e, null)}
          //         inputRef={inputRef}
          //         sx={{
          //           pr: 7,
          //           "& .MuiInputBase-input": {
          //             fontSize: "12px",
          //           },
          //         }}
          //       />
          //       <IconButton
          //         color="primary"
          //         onClick={(e) => handleSend(e, null)}
          //         sx={{
          //           position: "absolute",
          //           right: 18,
          //           top: "50%",
          //           transform: "translateY(-50%)",
          //           backgroundColor: "#0046be",
          //           color: "white",
          //           boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          //           "&:hover": {
          //             backgroundColor: "#003a9f",
          //           },
          //         }}
          //       >
          //         {loading ? (
          //           <CircularProgress size={20} sx={{ color: "#fff" }} />
          //         ) : (
          //           <SendIcon fontSize="small" />
          //         )}
          //       </IconButton>
          //     </Box>
          //   </Paper>
          // </motion.div>
          <SamarthAIDrawer
            drawer
            open={chatOpen}
            onClose={() => setChatOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;


